import { useMemo, useState } from 'react'
import {
  Banknote,
  Briefcase,
  CalendarCheck,
  CalendarClock,
  Clock,
  FileText,
  Gift,
  Handshake,
  HeartPulse,
  ListTree,
  PiggyBank,
  Scale,
  ShieldCheck,
  Stamp,
  TimerReset,
  UserPlus,
  Wallet,
  TrendingUp,
} from 'lucide-react'
import { Plus } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { SimpleListPage } from '@/components/SimpleListPage'
import { useAuth } from '@/auth/useAuth'
import { useToast } from '@/hooks/useToast'
import { useQueryClient } from '@tanstack/react-query'
import {
  AddHoursModal, LeaveRequestModal, ShiftModal, HolidayModal,
  EquityModal, EnrollmentModal, RetirementModal, HsaFsaModal, GarnishmentModal,
} from '@/features/payroll/WorkforceModals'
import {
  BenefitPlanModal, LeaveTypeModal, HolidayCalendarModal, SalaryStructureModal, PayGradeModal,
} from '@/features/payroll/WorkforceConfigModals'
import { decideLeaveRequest } from '@/features/payroll/extras'
import {
  useTimesheets,
  useLeaveRequests,
  useHolidays,
  useShifts,
  useSalaryStructures,
  usePayGrades,
  useEquityGrants,
  useBenefitPlans,
  useBenefitEnrollments,
  useRetirementAccounts,
  useHsaFsaAccounts,
  useAcaPeriods,
  useNachaBatches,
  useChecks,
  useGarnishments,
  usePaymentInstructions,
  useOnboarding,
  useCompCycles,
} from '@/features/payroll/extras'
import { formatPayrollMoney } from '@/features/payroll/format'
import { formatDate } from '@/lib/locale'
import { ReportRunner } from '@/components/ReportRunner'
import { PAYROLL_REPORTS } from '@/features/payroll/reports'

function statusBadge(status: string, mapping: Record<string, 'success' | 'warning' | 'secondary' | 'outline' | 'danger' | 'info'> = {}) {
  const variant = mapping[status] || 'secondary'
  return <Badge variant={variant}>{status.replace(/_/g, ' ')}</Badge>
}

const yn = (n: number) =>
  n > 0 ? <Badge variant="success">Yes</Badge> : <Badge variant="outline">No</Badge>

function useCreate(perm: string) {
  const { hasPermission } = useAuth()
  const [open, setOpen] = useState(false)
  return { can: hasPermission(perm as never), open, setOpen }
}

function NewButton({ label, onClick }: { label: string; onClick: () => void }) {
  return <Button onClick={onClick}><Plus className="mr-2 size-4" /> {label}</Button>
}

export function TimesheetsPage() {
  const { data, isLoading, error } = useTimesheets()
  const create = useCreate('payroll.time.manage')
  const columns = useMemo(
    () => [
      {
        accessorKey: 'employee_number',
        header: 'Employee',
        cell: ({ row }: { row: { original: { first_name: string; last_name: string; employee_number: string } } }) =>
          `${row.original.first_name} ${row.original.last_name} (${row.original.employee_number})`,
      },
      {
        accessorKey: 'period_start',
        header: 'Period',
        cell: ({ row }: { row: { original: { period_start: string | null; period_end: string | null } } }) =>
          row.original.period_start ? `${formatDate(row.original.period_start)} → ${formatDate(row.original.period_end!)}` : '-',
      },
      { accessorKey: 'total_hours', header: 'Hours' },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }: { row: { original: { status: string } } }) =>
          statusBadge(row.original.status, { open: 'outline', submitted: 'warning', approved: 'success', rejected: 'danger', locked: 'secondary' }),
      },
      {
        accessorKey: 'approved_at',
        header: 'Approved',
        cell: ({ row }: { row: { original: { approved_at: string | null } } }) =>
          row.original.approved_at ? formatDate(row.original.approved_at) : '-',
      },
    ],
    [],
  )
  return (
    <SimpleListPage
      title="Timesheets"
      description="Track regular, overtime, double-time, PTO, and shift-differential hours by pay period. Auto-feeds into pay run calculations."
      icon={Clock}
      rows={data?.data}
      isLoading={isLoading}
      error={error}
      columns={columns}
      actions={create.can ? <NewButton label="Log hours" onClick={() => create.setOpen(true)} /> : undefined}
      afterTable={create.open ? <AddHoursModal open={create.open} onOpenChange={create.setOpen} /> : null}
      emptyTitle="No timesheets yet"
      emptyDescription="Log hours for an hourly employee here - the pay-run calculation engine reads approved attendance for the period. Salaried employees don't need timesheets."
    />
  )
}

export function LeavePage() {
  const { data, isLoading, error } = useLeaveRequests()
  const { hasPermission } = useAuth()
  const { toast } = useToast()
  const qc = useQueryClient()
  const { user } = useAuth()
  const create = useCreate('payroll.leave.manage')
  const canApprove = hasPermission('payroll.leave.approve')
  const canManage = hasPermission('payroll.leave.manage')
  const [busyId, setBusyId] = useState<string | null>(null)
  const [typeOpen, setTypeOpen] = useState(false)

  async function decide(id: string, decision: 'approve' | 'reject') {
    setBusyId(id)
    try {
      await decideLeaveRequest(id, decision)
      if (user?.tenantId) qc.invalidateQueries({ queryKey: ['tenant', user.tenantId, 'payroll'] })
      toast({ title: decision === 'approve' ? 'Leave approved' : 'Leave rejected' })
    } catch (err) {
      toast({ title: 'Action failed', description: err instanceof Error ? err.message : String(err), variant: 'destructive' })
    } finally {
      setBusyId(null)
    }
  }

  const columns = useMemo(
    () => [
      {
        accessorKey: 'employee_number',
        header: 'Employee',
        cell: ({ row }: { row: { original: { first_name: string; last_name: string } } }) =>
          `${row.original.first_name} ${row.original.last_name}`,
      },
      { accessorKey: 'leave_type_label', header: 'Type' },
      {
        accessorKey: 'start_date',
        header: 'Period',
        cell: ({ row }: { row: { original: { start_date: string; end_date: string } } }) =>
          `${formatDate(row.original.start_date)} → ${formatDate(row.original.end_date)}`,
      },
      { accessorKey: 'hours_requested', header: 'Hours' },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }: { row: { original: { status: string } } }) =>
          statusBadge(row.original.status, { pending: 'warning', approved: 'success', rejected: 'danger', cancelled: 'secondary', taken: 'info' }),
      },
      {
        accessorKey: 'id',
        header: '',
        cell: ({ row }: { row: { original: { id: string; status: string } } }) =>
          canApprove && row.original.status === 'pending' ? (
            <div className="flex items-center gap-1">
              <Button size="sm" variant="ghost" disabled={busyId === row.original.id} onClick={() => decide(row.original.id, 'approve')}>Approve</Button>
              <Button size="sm" variant="ghost" disabled={busyId === row.original.id} onClick={() => decide(row.original.id, 'reject')}>Reject</Button>
            </div>
          ) : null,
      },
    ],
    [canApprove, busyId],
  )
  return (
    <SimpleListPage
      title="Leave & PTO"
      description="Leave requests across all leave types. Accrual rules + balances are kept per employee per leave type."
      icon={TimerReset}
      rows={data?.data}
      isLoading={isLoading}
      error={error}
      columns={columns}
      actions={canManage ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="secondary" onClick={() => setTypeOpen(true)}><Plus className="mr-2 size-4" /> New leave type</Button>
          <NewButton label="Request leave" onClick={() => create.setOpen(true)} />
        </div>
      ) : undefined}
      afterTable={
        <>
          {create.open ? <LeaveRequestModal open={create.open} onOpenChange={create.setOpen} /> : null}
          {typeOpen ? <LeaveTypeModal open={typeOpen} onOpenChange={setTypeOpen} /> : null}
        </>
      }
      emptyTitle="No leave requests yet"
      emptyDescription="Submit a leave request for an employee; managers approve or reject from this list."
    />
  )
}

export function HolidaysPage() {
  const { data, isLoading, error } = useHolidays()
  const create = useCreate('payroll.holidays.manage')
  const [calOpen, setCalOpen] = useState(false)
  const columns = useMemo(
    () => [
      {
        accessorKey: 'holiday_date',
        header: 'Date',
        cell: ({ row }: { row: { original: { holiday_date: string } } }) => formatDate(row.original.holiday_date),
      },
      { accessorKey: 'label', header: 'Holiday' },
      { accessorKey: 'calendar_name', header: 'Calendar' },
      {
        accessorKey: 'country',
        header: 'Jurisdiction',
        cell: ({ row }: { row: { original: { country: string; state_region: string | null } } }) =>
          row.original.state_region ? `${row.original.country}-${row.original.state_region}` : row.original.country,
      },
      {
        accessorKey: 'is_paid',
        header: 'Paid',
        cell: ({ row }: { row: { original: { is_paid: boolean; pay_multiplier: string } } }) =>
          row.original.is_paid ? <Badge variant="success">{Number(row.original.pay_multiplier).toFixed(2)}×</Badge> : <Badge variant="outline">Unpaid</Badge>,
      },
    ],
    [],
  )
  return (
    <SimpleListPage
      title="Holidays"
      description="Federal, state, and company holidays. Pay multipliers (1.0× / 1.5× / 2.0×) feed the calculation engine on holiday work."
      icon={CalendarCheck}
      rows={data?.data}
      isLoading={isLoading}
      error={error}
      columns={columns}
      actions={create.can ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="secondary" onClick={() => setCalOpen(true)}><Plus className="mr-2 size-4" /> New calendar</Button>
          <NewButton label="Add holiday" onClick={() => create.setOpen(true)} />
        </div>
      ) : undefined}
      afterTable={
        <>
          {create.open ? <HolidayModal open={create.open} onOpenChange={create.setOpen} /> : null}
          {calOpen ? <HolidayCalendarModal open={calOpen} onOpenChange={setCalOpen} /> : null}
        </>
      }
      emptyTitle="No holidays configured"
      emptyDescription="Create a holiday calendar for your region and add the federal + state observances your tenant honors."
    />
  )
}

export function SchedulesPage() {
  const { data, isLoading, error } = useShifts()
  const create = useCreate('payroll.schedules.manage')
  const columns = useMemo(
    () => [
      { accessorKey: 'name', header: 'Shift' },
      {
        accessorKey: 'shift_kind',
        header: 'Kind',
        cell: ({ row }: { row: { original: { shift_kind: string } } }) => (
          <Badge variant="outline">{row.original.shift_kind.replace(/_/g, ' ')}</Badge>
        ),
      },
      {
        accessorKey: 'start_time',
        header: 'Window',
        cell: ({ row }: { row: { original: { start_time: string; end_time: string; crosses_midnight: boolean } } }) =>
          `${row.original.start_time.slice(0, 5)} – ${row.original.end_time.slice(0, 5)}${row.original.crosses_midnight ? ' (overnight)' : ''}`,
      },
      {
        accessorKey: 'break_minutes',
        header: 'Breaks',
        cell: ({ row }: { row: { original: { break_minutes: number; paid_break_minutes: number } } }) =>
          `${row.original.break_minutes}m (${row.original.paid_break_minutes}m paid)`,
      },
      {
        accessorKey: 'shift_diff_pct',
        header: 'Differential',
        cell: ({ row }: { row: { original: { shift_diff_pct: string } } }) =>
          Number(row.original.shift_diff_pct) > 0 ? `${row.original.shift_diff_pct}%` : '-',
      },
      { accessorKey: 'assigned_count', header: 'Assigned' },
    ],
    [],
  )
  return (
    <SimpleListPage
      title="Schedules & Shifts"
      description="Fixed, rotating, split, and on-call shifts with differential pay (night, weekend, holiday). Drives shift-premium calculations on pay runs."
      icon={CalendarClock}
      rows={data?.data}
      isLoading={isLoading}
      error={error}
      columns={columns}
      actions={create.can ? <NewButton label="New shift" onClick={() => create.setOpen(true)} /> : undefined}
      afterTable={create.open ? <ShiftModal open={create.open} onOpenChange={create.setOpen} /> : null}
      emptyTitle="No shift schedules yet"
      emptyDescription="Define your shift patterns (morning, evening, overnight) with differential pay percentages so the calculation engine can apply them automatically."
    />
  )
}

export function SalaryStructuresPage() {
  const { data, isLoading, error } = useSalaryStructures()
  const create = useCreate('payroll.compensation.manage')
  const columns = useMemo(
    () => [
      { accessorKey: 'name', header: 'Name' },
      { accessorKey: 'description', header: 'Description' },
      { accessorKey: 'currency', header: 'Currency' },
      { accessorKey: 'component_count', header: 'Components' },
      {
        accessorKey: 'is_template',
        header: 'Type',
        cell: ({ row }: { row: { original: { is_template: boolean } } }) =>
          row.original.is_template ? <Badge variant="outline">Template</Badge> : <Badge variant="success">Live</Badge>,
      },
    ],
    [],
  )
  return (
    <SimpleListPage
      title="Salary Structures"
      description="Reusable templates of pay components (base + bonus + differentials + deductions). Apply a structure when creating a new salary revision for an employee."
      icon={ListTree}
      rows={data?.data}
      isLoading={isLoading}
      error={error}
      columns={columns}
      actions={create.can ? <NewButton label="New structure" onClick={() => create.setOpen(true)} /> : undefined}
      afterTable={create.open ? <SalaryStructureModal open={create.open} onOpenChange={create.setOpen} /> : null}
      emptyTitle="No salary structures yet"
      emptyDescription="Create structure templates for the common offer shapes (Engineer L3, Manager, Sales Rep, etc.)."
    />
  )
}

export function PayGradesPage() {
  const { data, isLoading, error } = usePayGrades()
  const create = useCreate('payroll.grades.manage')
  const columns = useMemo(
    () => [
      { accessorKey: 'grade_code', header: 'Code' },
      { accessorKey: 'label', header: 'Grade' },
      {
        accessorKey: 'min_minor_units',
        header: 'Min',
        cell: ({ row }: { row: { original: { min_minor_units: string; currency: string } } }) =>
          formatPayrollMoney(row.original.min_minor_units, row.original.currency),
      },
      {
        accessorKey: 'mid_minor_units',
        header: 'Mid',
        cell: ({ row }: { row: { original: { mid_minor_units: string; currency: string } } }) =>
          formatPayrollMoney(row.original.mid_minor_units, row.original.currency),
      },
      {
        accessorKey: 'max_minor_units',
        header: 'Max',
        cell: ({ row }: { row: { original: { max_minor_units: string; currency: string } } }) =>
          formatPayrollMoney(row.original.max_minor_units, row.original.currency),
      },
      {
        accessorKey: 'effective_from',
        header: 'Effective',
        cell: ({ row }: { row: { original: { effective_from: string } } }) => formatDate(row.original.effective_from),
      },
    ],
    [],
  )
  return (
    <SimpleListPage
      title="Pay Grades & Bands"
      description="Min / mid / max compensation bands per grade, with step structures for union and government compliance. Used for offer ranges and compensation analytics."
      icon={TrendingUp}
      rows={data?.data}
      isLoading={isLoading}
      error={error}
      columns={columns}
      actions={create.can ? <NewButton label="New grade" onClick={() => create.setOpen(true)} /> : undefined}
      afterTable={create.open ? <PayGradeModal open={create.open} onOpenChange={create.setOpen} /> : null}
      emptyTitle="No pay grades yet"
      emptyDescription="Set up grade bands (e.g., Engineer 1 / 2 / 3) so offers stay within market range and comp reviews can use them as guardrails."
    />
  )
}

export function EquityPage() {
  const { data, isLoading, error } = useEquityGrants()
  const create = useCreate('payroll.equity.manage')
  const columns = useMemo(
    () => [
      { accessorKey: 'grant_number', header: 'Grant #' },
      {
        accessorKey: 'employee_number',
        header: 'Employee',
        cell: ({ row }: { row: { original: { first_name: string; last_name: string } } }) =>
          `${row.original.first_name} ${row.original.last_name}`,
      },
      {
        accessorKey: 'grant_type',
        header: 'Type',
        cell: ({ row }: { row: { original: { grant_type: string } } }) => (
          <Badge variant="outline">{row.original.grant_type}</Badge>
        ),
      },
      { accessorKey: 'shares', header: 'Shares' },
      {
        accessorKey: 'grant_date',
        header: 'Granted',
        cell: ({ row }: { row: { original: { grant_date: string } } }) => formatDate(row.original.grant_date),
      },
      {
        accessorKey: 'vested_tranches',
        header: 'Vested',
        cell: ({ row }: { row: { original: { vested_tranches: number; total_tranches: number } } }) =>
          `${row.original.vested_tranches} / ${row.original.total_tranches}`,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }: { row: { original: { status: string } } }) =>
          statusBadge(row.original.status, { active: 'success', fully_vested: 'success', accelerated: 'info', cancelled: 'secondary', forfeited: 'danger', exercised: 'info' }),
      },
    ],
    [],
  )
  return (
    <SimpleListPage
      title="Equity Grants"
      description="RSU, ISO, NSO, ESPP, RSA, PSU, SAR, and Phantom Stock grants. Vesting schedules + tranches drive expense recognition and W-2 income."
      icon={Gift}
      rows={data?.data}
      isLoading={isLoading}
      error={error}
      columns={columns}
      actions={create.can ? <NewButton label="New grant" onClick={() => create.setOpen(true)} /> : undefined}
      afterTable={create.open ? <EquityModal open={create.open} onOpenChange={create.setOpen} /> : null}
      emptyTitle="No equity grants yet"
      emptyDescription="Add equity grants here. The vesting engine handles cliffs, monthly/quarterly vesting, and acceleration on termination."
    />
  )
}

export function ReviewCyclesPage() {
  const { data, isLoading, error } = useCompCycles()
  const columns = useMemo<Array<{ accessorKey: string; header: string }>>(
    () => [
      { accessorKey: 'name', header: 'Cycle' },
      { accessorKey: 'period_start', header: 'Starts' },
      { accessorKey: 'period_end', header: 'Ends' },
      { accessorKey: 'status', header: 'Status' },
    ],
    [],
  )
  return (
    <SimpleListPage
      title="Compensation Review Cycles"
      description="Annual merit, promotion, and bonus planning windows. Each cycle drives bulk salary revisions through the approval engine."
      icon={CalendarClock}
      rows={data?.data}
      isLoading={isLoading}
      error={error}
      columns={columns as never}
      emptyTitle="No review cycles yet"
      emptyDescription="No compensation review cycles yet. A review cycle groups merit increases, promotions, and bonus changes into a single batch that routes through the approval workflow before the salary revisions take effect."
    />
  )
}

export function BenefitPlansPage() {
  const { data, isLoading, error } = useBenefitPlans()
  const create = useCreate('payroll.benefits.manage')
  const columns = useMemo(
    () => [
      { accessorKey: 'plan_code', header: 'Code' },
      { accessorKey: 'plan_name', header: 'Plan' },
      {
        accessorKey: 'plan_kind',
        header: 'Kind',
        cell: ({ row }: { row: { original: { plan_kind: string } } }) => (
          <Badge variant="outline">{row.original.plan_kind.replace(/_/g, ' ')}</Badge>
        ),
      },
      { accessorKey: 'carrier', header: 'Carrier' },
      {
        accessorKey: 'employer_share_pct',
        header: 'Employer share',
        cell: ({ row }: { row: { original: { employer_share_pct: string | null } } }) =>
          row.original.employer_share_pct ? `${row.original.employer_share_pct}%` : '-',
      },
      {
        accessorKey: 'pretax',
        header: 'Pre-tax',
        cell: ({ row }: { row: { original: { pretax: boolean } } }) =>
          row.original.pretax ? <Badge variant="success">Yes</Badge> : <Badge variant="outline">No</Badge>,
      },
      {
        accessorKey: 'is_active',
        header: 'Status',
        cell: ({ row }: { row: { original: { is_active: boolean } } }) =>
          row.original.is_active ? <Badge variant="success">Active</Badge> : <Badge variant="secondary">Inactive</Badge>,
      },
    ],
    [],
  )
  return (
    <SimpleListPage
      title="Benefit Plans"
      description="Medical, dental, vision, HSA/FSA, 401(k), life, disability, COBRA, and voluntary benefits. Each plan defines eligibility rules and the employer/employee cost split."
      icon={HeartPulse}
      rows={data?.data}
      isLoading={isLoading}
      error={error}
      columns={columns}
      actions={create.can ? <NewButton label="New plan" onClick={() => create.setOpen(true)} /> : undefined}
      afterTable={create.open ? <BenefitPlanModal open={create.open} onOpenChange={create.setOpen} /> : null}
      emptyTitle="No benefit plans yet"
      emptyDescription="Add the carrier plans your tenant offers, then enroll employees from the Enrollment page."
    />
  )
}

export function BenefitEnrollmentPage() {
  const { data, isLoading, error } = useBenefitEnrollments()
  const create = useCreate('payroll.benefits.enroll')
  const columns = useMemo(
    () => [
      {
        accessorKey: 'employee_id',
        header: 'Employee',
        cell: ({ row }: { row: { original: { first_name: string; last_name: string } } }) =>
          `${row.original.first_name} ${row.original.last_name}`,
      },
      { accessorKey: 'plan_name', header: 'Plan' },
      {
        accessorKey: 'plan_kind',
        header: 'Kind',
        cell: ({ row }: { row: { original: { plan_kind: string } } }) => (
          <Badge variant="outline">{row.original.plan_kind.replace(/_/g, ' ')}</Badge>
        ),
      },
      { accessorKey: 'coverage_tier', header: 'Coverage' },
      {
        accessorKey: 'employer_amount_minor_units',
        header: 'Employer',
        cell: ({ row }: { row: { original: { employer_amount_minor_units: string; currency: string } } }) =>
          formatPayrollMoney(row.original.employer_amount_minor_units, row.original.currency),
      },
      {
        accessorKey: 'employee_amount_minor_units',
        header: 'Employee',
        cell: ({ row }: { row: { original: { employee_amount_minor_units: string; currency: string } } }) =>
          formatPayrollMoney(row.original.employee_amount_minor_units, row.original.currency),
      },
      {
        accessorKey: 'effective_from',
        header: 'Effective',
        cell: ({ row }: { row: { original: { effective_from: string } } }) => formatDate(row.original.effective_from),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }: { row: { original: { status: string } } }) =>
          statusBadge(row.original.status, { active: 'success', waived: 'secondary', terminated: 'secondary', pending: 'warning' }),
      },
    ],
    [],
  )
  return (
    <SimpleListPage
      title="Benefit Enrollments"
      description="Per-employee benefit elections. Created during open enrollment, on hire, or following a qualifying life event."
      icon={Stamp}
      rows={data?.data}
      isLoading={isLoading}
      error={error}
      columns={columns}
      actions={create.can ? <NewButton label="Enroll employee" onClick={() => create.setOpen(true)} /> : undefined}
      afterTable={create.open ? <EnrollmentModal open={create.open} onOpenChange={create.setOpen} /> : null}
      emptyTitle="No benefit enrollments yet"
      emptyDescription="Once benefit plans are configured, employees enroll through self-service or admins enroll them on hire."
    />
  )
}

export function RetirementPage() {
  const { data, isLoading, error } = useRetirementAccounts()
  const create = useCreate('payroll.retirement.manage')
  const columns = useMemo(
    () => [
      {
        accessorKey: 'employee_number',
        header: 'Employee',
        cell: ({ row }: { row: { original: { first_name: string; last_name: string } } }) =>
          `${row.original.first_name} ${row.original.last_name}`,
      },
      {
        accessorKey: 'account_kind',
        header: 'Account',
        cell: ({ row }: { row: { original: { account_kind: string } } }) => (
          <Badge variant="outline">{row.original.account_kind.replace(/_/g, ' ')}</Badge>
        ),
      },
      { accessorKey: 'provider', header: 'Provider' },
      {
        accessorKey: 'ytd_employee_contrib_minor_units',
        header: 'YTD employee',
        cell: ({ row }: { row: { original: { ytd_employee_contrib_minor_units: string } } }) =>
          formatPayrollMoney(row.original.ytd_employee_contrib_minor_units),
      },
      {
        accessorKey: 'ytd_employer_contrib_minor_units',
        header: 'YTD employer',
        cell: ({ row }: { row: { original: { ytd_employer_contrib_minor_units: string } } }) =>
          formatPayrollMoney(row.original.ytd_employer_contrib_minor_units),
      },
      {
        accessorKey: 'ytd_catchup_minor_units',
        header: 'Catch-up',
        cell: ({ row }: { row: { original: { ytd_catchup_minor_units: string; catchup_eligible: boolean } } }) =>
          row.original.catchup_eligible
            ? formatPayrollMoney(row.original.ytd_catchup_minor_units)
            : '-',
      },
    ],
    [],
  )
  return (
    <SimpleListPage
      title="401(k) / Retirement"
      description="Traditional 401(k), Roth 401(k), SIMPLE IRA, 403(b), 457(b), and Solo 401(k) accounts with YTD contribution tracking against IRS annual limits."
      icon={PiggyBank}
      rows={data?.data}
      isLoading={isLoading}
      error={error}
      columns={columns}
      actions={create.can ? <NewButton label="Add account" onClick={() => create.setOpen(true)} /> : undefined}
      afterTable={create.open ? <RetirementModal open={create.open} onOpenChange={create.setOpen} /> : null}
      emptyTitle="No retirement accounts yet"
      emptyDescription="Add a retirement account per employee. Contribution elections (% or fixed amount, pre-tax / Roth split) drive automatic payroll deductions."
    />
  )
}

export function HsaFsaPage() {
  const { data, isLoading, error } = useHsaFsaAccounts()
  const create = useCreate('payroll.hsa_fsa.manage')
  const columns = useMemo(
    () => [
      {
        accessorKey: 'employee_number',
        header: 'Employee',
        cell: ({ row }: { row: { original: { first_name: string; last_name: string } } }) =>
          `${row.original.first_name} ${row.original.last_name}`,
      },
      {
        accessorKey: 'account_kind',
        header: 'Account',
        cell: ({ row }: { row: { original: { account_kind: string } } }) => (
          <Badge variant="outline">{row.original.account_kind.replace(/_/g, ' ')}</Badge>
        ),
      },
      {
        accessorKey: 'annual_election_minor_units',
        header: 'Annual election',
        cell: ({ row }: { row: { original: { annual_election_minor_units: string; currency: string } } }) =>
          formatPayrollMoney(row.original.annual_election_minor_units, row.original.currency),
      },
      {
        accessorKey: 'ytd_contrib_minor_units',
        header: 'YTD contributed',
        cell: ({ row }: { row: { original: { ytd_contrib_minor_units: string; currency: string } } }) =>
          formatPayrollMoney(row.original.ytd_contrib_minor_units, row.original.currency),
      },
      {
        accessorKey: 'ytd_disbursed_minor_units',
        header: 'YTD disbursed',
        cell: ({ row }: { row: { original: { ytd_disbursed_minor_units: string; currency: string } } }) =>
          formatPayrollMoney(row.original.ytd_disbursed_minor_units, row.original.currency),
      },
    ],
    [],
  )
  return (
    <SimpleListPage
      title="HSA / FSA"
      description="Health Savings Accounts, Health FSAs, Dependent Care FSAs, Limited Purpose FSAs, and Commuter accounts. Pre-tax deductions feed payroll automatically."
      icon={Wallet}
      rows={data?.data}
      isLoading={isLoading}
      error={error}
      columns={columns}
      actions={create.can ? <NewButton label="Add account" onClick={() => create.setOpen(true)} /> : undefined}
      afterTable={create.open ? <HsaFsaModal open={create.open} onOpenChange={create.setOpen} /> : null}
      emptyTitle="No HSA/FSA accounts yet"
      emptyDescription="Add an account per employee with their annual election; pre-tax deductions feed payroll automatically."
    />
  )
}

export function AcaPage() {
  const { data, isLoading, error } = useAcaPeriods()
  const columns = useMemo(
    () => [
      {
        accessorKey: 'employee_id',
        header: 'Employee',
        cell: ({ row }: { row: { original: { first_name: string; last_name: string; employee_number: string } } }) =>
          `${row.original.first_name} ${row.original.last_name} (${row.original.employee_number})`,
      },
      {
        accessorKey: 'period_kind',
        header: 'Period',
        cell: ({ row }: { row: { original: { period_kind: string } } }) => (
          <Badge variant="outline">{row.original.period_kind}</Badge>
        ),
      },
      {
        accessorKey: 'period_start',
        header: 'Window',
        cell: ({ row }: { row: { original: { period_start: string; period_end: string } } }) =>
          `${formatDate(row.original.period_start)} → ${formatDate(row.original.period_end)}`,
      },
      { accessorKey: 'hours_total', header: 'Hours' },
      {
        accessorKey: 'full_time_status',
        header: 'FT status',
        cell: ({ row }: { row: { original: { full_time_status: boolean } } }) =>
          row.original.full_time_status ? <Badge variant="success">Full-time</Badge> : <Badge variant="secondary">Variable</Badge>,
      },
      { accessorKey: 'offer_kind', header: 'Offer' },
      { accessorKey: 'affordability_safe_harbor', header: 'Safe harbor' },
    ],
    [],
  )
  return (
    <SimpleListPage
      title="ACA Tracking"
      description="Affordable Care Act measurement / administrative / stability windows. Full-time-equivalent rolls up into 1094-C + 1095-C at year-end."
      icon={ShieldCheck}
      rows={data?.data}
      isLoading={isLoading}
      error={error}
      columns={columns}
      emptyTitle="No ACA periods yet"
      emptyDescription="ACA tracking runs automatically once you opt in to coverage tracking under Settings. The workflow engine rolls measurement periods into 1095-C generation each January."
    />
  )
}

export function NachaPage() {
  const { data, isLoading, error } = useNachaBatches()
  const columns = useMemo(
    () => [
      { accessorKey: 'batch_number', header: 'Batch #' },
      { accessorKey: 'run_number', header: 'Pay run' },
      {
        accessorKey: 'effective_entry_date',
        header: 'Effective',
        cell: ({ row }: { row: { original: { effective_entry_date: string } } }) => formatDate(row.original.effective_entry_date),
      },
      {
        accessorKey: 'sec_code',
        header: 'SEC',
        cell: ({ row }: { row: { original: { sec_code: string } } }) => (
          <Badge variant="outline">{row.original.sec_code}</Badge>
        ),
      },
      { accessorKey: 'entry_count', header: 'Entries' },
      {
        accessorKey: 'credit_total_minor_units',
        header: 'Credits',
        cell: ({ row }: { row: { original: { credit_total_minor_units: string } } }) =>
          formatPayrollMoney(row.original.credit_total_minor_units),
      },
      {
        accessorKey: 'debit_total_minor_units',
        header: 'Debits',
        cell: ({ row }: { row: { original: { debit_total_minor_units: string } } }) =>
          formatPayrollMoney(row.original.debit_total_minor_units),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }: { row: { original: { status: string } } }) =>
          statusBadge(row.original.status, { draft: 'outline', generated: 'info', transmitted: 'info', acknowledged: 'info', settled: 'success', returned: 'danger', cancelled: 'secondary', failed: 'danger' }),
      },
    ],
    [],
  )
  return (
    <SimpleListPage
      title="Direct Deposit (NACHA)"
      description="94-byte NACHA batch files for ODFI transmission. PPD for W-2 employees, CCD for contractors. Prenote, return-code handling, and same-day ACH built in."
      icon={Banknote}
      rows={data?.data}
      isLoading={isLoading}
      error={error}
      columns={columns}
      emptyTitle="No NACHA batches yet"
      emptyDescription="NACHA batches are generated when a pay run is posted. Bank sponsorship (ODFI relationship) needed before transmit; configure under Settings."
    />
  )
}

export function ChecksPage() {
  const { data, isLoading, error } = useChecks()
  const columns = useMemo(
    () => [
      { accessorKey: 'check_number', header: 'Check #' },
      { accessorKey: 'payee_name', header: 'Payee' },
      {
        accessorKey: 'amount_minor_units',
        header: 'Amount',
        cell: ({ row }: { row: { original: { amount_minor_units: string; currency: string } } }) =>
          formatPayrollMoney(row.original.amount_minor_units, row.original.currency),
      },
      {
        accessorKey: 'issued_date',
        header: 'Issued',
        cell: ({ row }: { row: { original: { issued_date: string } } }) => formatDate(row.original.issued_date),
      },
      {
        accessorKey: 'cleared_at',
        header: 'Cleared',
        cell: ({ row }: { row: { original: { cleared_at: string | null; voided_at: string | null } } }) =>
          row.original.voided_at
            ? <Badge variant="danger">Voided</Badge>
            : row.original.cleared_at
              ? <Badge variant="success">Cleared {formatDate(row.original.cleared_at)}</Badge>
              : <Badge variant="outline">Outstanding</Badge>,
      },
    ],
    [],
  )
  return (
    <SimpleListPage
      title="Checks"
      description="MICR check printing with positive-pay file generation. Use for paper paychecks, garnishment remittance, and tax-payment fallback."
      icon={FileText}
      rows={data?.data}
      isLoading={isLoading}
      error={error}
      columns={columns}
      emptyTitle="No checks issued"
      emptyDescription="When a payment instruction uses method='check', a PDF check is generated here and added to your positive-pay file."
    />
  )
}

export function GarnishmentsPage() {
  const { data, isLoading, error } = useGarnishments()
  const create = useCreate('payroll.payments.batch')
  const columns = useMemo(
    () => [
      {
        accessorKey: 'employee_number',
        header: 'Employee',
        cell: ({ row }: { row: { original: { first_name: string; last_name: string } } }) =>
          `${row.original.first_name} ${row.original.last_name}`,
      },
      {
        accessorKey: 'kind',
        header: 'Type',
        cell: ({ row }: { row: { original: { kind: string } } }) =>
          <Badge variant="outline">{row.original.kind.replace(/_/g, ' ')}</Badge>,
      },
      { accessorKey: 'court_priority', header: 'Priority' },
      {
        accessorKey: 'per_period_amount_minor_units',
        header: 'Per period',
        cell: ({ row }: { row: { original: { per_period_amount_minor_units: string | null; per_period_pct: string | null; currency: string } } }) =>
          row.original.per_period_amount_minor_units
            ? formatPayrollMoney(row.original.per_period_amount_minor_units, row.original.currency)
            : row.original.per_period_pct
              ? `${row.original.per_period_pct}%`
              : '-',
      },
      {
        accessorKey: 'balance_owed_minor_units',
        header: 'Balance owed',
        cell: ({ row }: { row: { original: { balance_owed_minor_units: string | null; currency: string } } }) =>
          row.original.balance_owed_minor_units
            ? formatPayrollMoney(row.original.balance_owed_minor_units, row.original.currency)
            : '-',
      },
      { accessorKey: 'remit_to_name', header: 'Remit to' },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }: { row: { original: { status: string } } }) =>
          statusBadge(row.original.status, { active: 'success', satisfied: 'secondary', released: 'secondary', suspended: 'warning', transferred: 'info' }),
      },
    ],
    [],
  )
  return (
    <SimpleListPage
      title="Garnishments"
      description="Court-ordered wage garnishments. The pay-run engine applies these in court-priority order within the CCPA disposable-income caps."
      icon={Scale}
      rows={data?.data}
      isLoading={isLoading}
      error={error}
      columns={columns}
      actions={create.can ? <NewButton label="Add garnishment" onClick={() => create.setOpen(true)} /> : undefined}
      afterTable={create.open ? <GarnishmentModal open={create.open} onOpenChange={create.setOpen} /> : null}
      emptyTitle="No garnishment orders"
      emptyDescription="Add a child-support, tax-levy, or creditor garnishment and the pay-run engine will withhold it automatically, capped to the employee's disposable earnings."
    />
  )
}

export function PaymentBatchesPage() {
  const { data, isLoading, error } = usePaymentInstructions()
  const columns = useMemo(
    () => [
      {
        accessorKey: 'method',
        header: 'Method',
        cell: ({ row }: { row: { original: { method: string } } }) => (
          <Badge variant="outline">{row.original.method.toUpperCase()}</Badge>
        ),
      },
      {
        accessorKey: 'employee_id',
        header: 'Payee',
        cell: ({ row }: { row: { original: { first_name: string | null; last_name: string | null; business_name: string | null; individual_first_name: string | null; individual_last_name: string | null } } }) =>
          row.original.first_name
            ? `${row.original.first_name} ${row.original.last_name}`
            : row.original.business_name
              ? row.original.business_name
              : `${row.original.individual_first_name ?? ''} ${row.original.individual_last_name ?? ''}`.trim() || '-',
      },
      {
        accessorKey: 'amount_minor_units',
        header: 'Amount',
        cell: ({ row }: { row: { original: { amount_minor_units: string; currency: string } } }) =>
          formatPayrollMoney(row.original.amount_minor_units, row.original.currency),
      },
      { accessorKey: 'trace_number', header: 'Trace #' },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }: { row: { original: { status: string; return_code: string | null } } }) =>
          row.original.return_code
            ? <Badge variant="danger">Returned {row.original.return_code}</Badge>
            : statusBadge(row.original.status, { pending: 'outline', batched: 'info', sent: 'info', settled: 'success', returned: 'danger', failed: 'danger', cancelled: 'secondary', voided: 'secondary' }),
      },
    ],
    [],
  )
  return (
    <SimpleListPage
      title="Payment Batches"
      description="All outbound payment instructions across ACH, checks, wires, paycards, RTP, and same-day ACH. Failed returns (R01–R99) handled by the recovery saga."
      icon={Handshake}
      rows={data?.data}
      isLoading={isLoading}
      error={error}
      columns={columns}
      emptyTitle="No payment instructions yet"
      emptyDescription="Payment instructions are created when a pay run is posted. Each one tracks its method, status, and acknowledgement."
    />
  )
}

export function PayrollReportsPage() {
  return (
    <ReportRunner
      title="Payroll Reports"
      description="Run any payroll report from posted pay runs, attendance, and employee data. View results inline and export to CSV or PDF."
      reports={PAYROLL_REPORTS}
    />
  )
}

export function OnboardingPage() {
  const { data, isLoading, error } = useOnboarding()
  const columns = useMemo(
    () => [
      { accessorKey: 'employee_number', header: 'Employee #' },
      {
        accessorKey: 'first_name',
        header: 'Name',
        cell: ({ row }: { row: { original: { first_name: string; last_name: string } } }) =>
          `${row.original.first_name} ${row.original.last_name}`,
      },
      {
        accessorKey: 'hire_date',
        header: 'Hire date',
        cell: ({ row }: { row: { original: { hire_date: string } } }) => formatDate(row.original.hire_date),
      },
      { accessorKey: 'work_state', header: 'State' },
      {
        accessorKey: 'has_i9',
        header: 'I-9',
        cell: ({ row }: { row: { original: { has_i9: number } } }) => yn(row.original.has_i9),
      },
      {
        accessorKey: 'has_w4',
        header: 'W-4',
        cell: ({ row }: { row: { original: { has_w4: number } } }) => yn(row.original.has_w4),
      },
      {
        accessorKey: 'has_dd',
        header: 'Direct deposit',
        cell: ({ row }: { row: { original: { has_dd: number; bank_count: number } } }) =>
          row.original.has_dd > 0 || row.original.bank_count > 0
            ? <Badge variant="success">Set up</Badge>
            : <Badge variant="warning">Pending</Badge>,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }: { row: { original: { status: string } } }) =>
          statusBadge(row.original.status, { active: 'success', pending_start: 'warning' }),
      },
    ],
    [],
  )
  return (
    <SimpleListPage
      title="Onboarding"
      description="Employees in their first 30 days or with pending-start status. Tracks I-9, W-4, direct-deposit, and bank-account setup."
      icon={UserPlus}
      rows={data?.data}
      isLoading={isLoading}
      error={error}
      columns={columns}
      emptyTitle="Nobody onboarding right now"
      emptyDescription="Add a new employee and they'll appear here until 30 days after their hire date so HR can chase down the missing I-9, W-4, and direct-deposit setup."
    />
  )
}

// Re-exports so the placeholder names keep their old import paths
export { Briefcase }
